import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import Container from 'common/Container';
import landingApiService from '../../../services/landingApi';
import { Who, Why, Benefits, Values } from '../TNOMediaKitCreator/components';
import AreYouReady from '../TNOMediaKitCreator/components/AreYouReady';
import {
  MercosurPodcastHero as ProposalHero,
  ProductionPhases,
  ProductionServices,
  DeliverablesMetrics,
  BonificacionSection,
  InvestmentSummary,
} from '../TNOMediaKitMercosurPodcast/components';

const LandingMediaKitMediaLabProduction = ({
  themeMode = 'light',
  proposalSlug,
  mediaLabContent,
}) => {
  const theme = useTheme();
  const isSm = useMediaQuery(theme.breakpoints.up('sm'), { defaultMatches: true });
  const [proposal, setProposal] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const data = await landingApiService.getProposalBySlugStatic(proposalSlug);
        if (!cancelled) setProposal(data);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [proposalSlug]);

  useEffect(() => {
    if (!proposal) return;
    if (proposal.metaTitle) document.title = proposal.metaTitle;
    const desc = document.querySelector('meta[name="description"]');
    if (desc && proposal.metaDescription) desc.setAttribute('content', proposal.metaDescription);
    return () => {
      document.title = 'TNO Media Kit | Anuncia con Nosotros';
    };
  }, [proposal]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <Typography color="textSecondary">Cargando propuesta...</Typography>
      </Box>
    );
  }

  if (!proposal?.content) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <Typography color="error">Propuesta no encontrada.</Typography>
      </Box>
    );
  }

  const content = proposal.content;
  const lab = mediaLabContent || {};

  return (
    <Box>
      <Container>
        <ProposalHero hero={content.hero} themeMode={themeMode} />
      </Container>

      {!content.hero?.skipWhoSection && lab.who && (
        <Container>
          <Who
            themeMode={themeMode}
            content={lab.who}
            forceContent
            hideLogo={Boolean(content.hero?.showTnoLogo)}
          />
        </Container>
      )}

      <Container
        sx={{
          backgroundImage: 'url("/images/millennials_stream.jpg")',
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'top',
          backgroundSize: 'cover',
          minHeight: '50vh',
        }}
        borderRadius={isSm ? 4 : 0}
      />

      <Container>
        <Why content={lab.why} />
      </Container>

      <Container>
        <Benefits themeMode={themeMode} content={lab.benefits} />
      </Container>

      <Container>
        <Values content={lab.values} />
      </Container>

      <Container>
        <ProductionPhases section={content.tnoEcosystem} />
      </Container>

      <Container>
        <ProductionServices section={content.productsExplained} />
      </Container>

      <Container>
        <DeliverablesMetrics metrics={content.metrics} section={content.metricsSection} />
      </Container>

      {content.bonificacion && (
        <Container>
          <BonificacionSection section={content.bonificacion} />
        </Container>
      )}

      {content.comercializacion && (
        <Container>
          <BonificacionSection section={content.comercializacion} />
        </Container>
      )}

      <Container>
        <InvestmentSummary summary={content.investmentSummary} />
      </Container>

      <Container>
        <AreYouReady content={lab.areYouReady} />
      </Container>
    </Box>
  );
};

LandingMediaKitMediaLabProduction.propTypes = {
  themeMode: PropTypes.string,
  proposalSlug: PropTypes.string.isRequired,
  mediaLabContent: PropTypes.shape({
    who: PropTypes.object,
    why: PropTypes.object,
    benefits: PropTypes.object,
    values: PropTypes.object,
    areYouReady: PropTypes.object,
  }).isRequired,
};

export default LandingMediaKitMediaLabProduction;
